/**
 * W91 (2026-06-10) — Reversal-Radar: Persistenz für Stage-4-Boden-Kandidaten.
 *
 * Der wertvollste Alarm für einen antizyklischen Trader ist "ein Quality-Wert
 * im Abwärtstrend zeigt erste Wende-Zeichen" (Kurs zurück über MA50 +
 * Momentum dreht = `antizyklisch_stabilizing` aus der Score-Engine). Dieses
 * Flag existierte seit Welle 15, verpuffte aber ungenutzt.
 *
 * Mechanik (gleiches Muster wie 'cockpit.conviction.cache' der Top-Picks):
 * Der Watchlist-Render (SymbolScoreCell) schreibt für jeden Wert im
 * Antizyklisch-Modus einen Eintrag in localStorage; verlässt ein Wert den
 * Modus (Stage 4 vorbei oder Long-Setup wieder da), wird der Eintrag
 * entfernt. Die Dashboard-Card (ReversalRadarCard) liest hier.
 *
 * Ehrliche Einschränkung: Datenquelle ist der Browser-Render der Watchlist —
 * ohne Watchlist-Besuch altern die Einträge (Anzeige zeigt das Alter; Einträge
 * >7 Tage werden ausgeblendet). Der Server-Score-Cron (W92) macht das später
 * lückenlos.
 */

export interface ReversalRadarEntry {
  symbol: string;
  layer: string;
  /** Gedeckelter Boden-Score (max 64). */
  score: number;
  /** True = erste Reversal-Zeichen (über MA50 + Momentum dreht). */
  stabilizing: boolean;
  /** Klartext der Boden-Bewertung. */
  summary: string;
  updated_at: string; // ISO
}

const KEY = 'cockpit.reversal.cache';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type RadarMap = Record<string, ReversalRadarEntry>;

function readMap(): RadarMap {
  try {
    return JSON.parse(window.localStorage.getItem(KEY) ?? '{}') as RadarMap;
  } catch {
    return {};
  }
}

/** Upsert (entry) oder Entfernen (null) eines Symbols — fire-and-forget. */
export function updateReversalRadar(
  symbol: string,
  entry: Omit<ReversalRadarEntry, 'symbol' | 'updated_at'> | null
): void {
  try {
    const map = readMap();
    const key = symbol.toUpperCase();
    if (entry === null) {
      if (!(key in map)) return; // kein Write nötig
      delete map[key];
    } else {
      map[key] = { ...entry, symbol: key, updated_at: new Date().toISOString() };
    }
    window.localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // localStorage voll/blockiert — Radar ist nice-to-have, nie fatal.
  }
}

function sortEntries(entries: ReversalRadarEntry[]): ReversalRadarEntry[] {
  return entries.sort((a, b) => {
    if (a.stabilizing !== b.stabilizing) return a.stabilizing ? -1 : 1;
    return b.score - a.score;
  });
}

/** Sortiert: Reversal-Zeichen zuerst, dann Boden-Score absteigend.
 *  Einträge älter als 7 Tage werden ausgefiltert (stale Render-Daten). */
export function loadReversalRadar(): ReversalRadarEntry[] {
  const cutoff = Date.now() - MAX_AGE_MS;
  return sortEntries(
    Object.values(readMap()).filter((e) => Date.parse(e.updated_at) >= cutoff)
  );
}

/**
 * W92 — DB-Variante: liest v5_reversal_radar (geschrieben vom täglichen
 * Server-Score-Cron, lückenlos auch ohne Watchlist-Besuch). Die Card nutzt
 * DB-first und fällt auf den localStorage-Cache zurück, wenn die DB (noch)
 * leer ist oder der Read scheitert.
 */
export async function fetchReversalRadarFromDb(
  signal?: AbortSignal
): Promise<ReversalRadarEntry[] | null> {
  try {
    const { supabase } = await import('@/lib/supabase');
    let q = supabase
      .from('v5_reversal_radar')
      .select('symbol, layer, score, stabilizing, summary, updated_at');
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error || !data) return null;
    const cutoff = Date.now() - MAX_AGE_MS;
    const entries = (data as ReversalRadarEntry[]).filter(
      (e) => Date.parse(e.updated_at) >= cutoff
    );
    return sortEntries(entries);
  } catch {
    return null;
  }
}
